import { createContext, useContext } from 'react';
import type { operations } from '@molchanovs/api-contract';
import type { HelpdeskEmailChannel, HelpdeskEmailDelivery } from '../api/email-delivery';
import type { AiReplyResponse } from '../api/ai-reply';
export const helpdeskDeletionRefreshMs = 2000;
export type CustomerTask = Extract<
  operations['getApiV1Tasks']['responses'][200]['content']['application/json']['items'][number],
  { isClosed: boolean }
>;
export type CustomerThread = Extract<
  operations['getApiV1TasksByTaskUuidReplies']['responses'][200]['content']['application/json'],
  { task: { isClosed: boolean } }
>;
export type RequesterBootstrapResponse = Extract<
  operations['getApiV1TasksFormOptions']['responses'][200]['content']['application/json'],
  { data: { requester: unknown } }
>;
export type CustomerTaskListResponse = Extract<
  operations['getApiV1Tasks']['responses'][200]['content']['application/json'],
  { items: readonly { isClosed: boolean }[] }
>;
export type HelpdeskOption = NonNullable<CustomerTask['status']>;
export type PresentationTask = Omit<CustomerTask, 'isClosed' | 'author'> & {
  isClosed?: boolean;
  emailChannel?: HelpdeskEmailChannel | null;
  emailDelivery?: HelpdeskEmailDelivery | null;
  deletionState?: { status: 'pending' | 'failed' } | null;
  author: { displayName: string | null; id?: string; avatarUrl?: string | null } | null;
};
export type PresentationMessage = PresentationTask & {
  contactEmail?: string | null;
  files: CustomerThread['task']['files'];
  parentId?: string;
  kind?: 'support-reply' | 'user-reply' | 'ai-reply' | 'internal-comment';
  signature?: string | null;
};
export interface PresentationThread {
  task: PresentationMessage;
  items: readonly PresentationMessage[];
  meta: CustomerThread['meta'];
}
export interface FormInput {
  title: string;
  message: string;
  originId: string;
  feedbackCategoryId?: string;
  privacyAcknowledged: true;
  requester?: { contactEmail: string } | { customerId: string };
}
export interface HelpdeskRuntime {
  audience: 'staff' | 'requester';
  scope: string;
  generateReplyDraft?(id: string, key: string): Promise<AiReplyResponse>;
  getThread(id: string, page: number, signal?: AbortSignal): Promise<PresentationThread>;
  create(
    input: FormInput,
    key: string,
    files: readonly File[]
  ): Promise<{ data: PresentationTask }>;
  reply(
    id: string,
    input: {
      message: string;
      kind?: 'support-reply' | 'internal-comment';
      delivery?: { channelId: string };
    },
    key: string,
    files: readonly File[]
  ): Promise<{ data: PresentationTask }>;
  getFile(id: string, fileRef: string, signal?: AbortSignal): Promise<Blob>;
  dispose(): void;
}
export interface RequesterHelpdeskRuntime extends HelpdeskRuntime {
  bootstrap(options?: {
    verifyGuestCookie?: boolean;
    expectedAccount?: boolean;
    signal?: AbortSignal;
  }): Promise<RequesterBootstrapResponse>;
  list(options: {
    page: number;
    pageSize?: number;
    signal?: AbortSignal;
  }): Promise<CustomerTaskListResponse>;
}
export const HelpdeskRuntimeContext = createContext<HelpdeskRuntime | null>(null);
export function useHelpdeskRuntime(): HelpdeskRuntime {
  const runtime = useContext(HelpdeskRuntimeContext);
  if (!runtime) throw new Error('HelpdeskRuntimeProvider is required');
  return runtime;
}
export function runtimeThreadQuery(
  runtime: HelpdeskRuntime,
  scope: string,
  id: string,
  query: { page: number; pageSize?: number }
) {
  return {
    queryKey: ['helpdesk', scope, 'thread', id, query] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) => runtime.getThread(id, query.page, signal),
    retry: false as const,
    staleTime: 0,
  };
}
